import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Send, Paperclip, X, MessageSquare, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { usePushNotifications } from "@/lib/usePushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable chat panel for the shared client/admin/team thread.
 * Props:
 *   clientId    string  - the client thread id
 *   token       string  - bearer token (admin/client/team)
 *   actorType   "admin" | "client" | "team"
 *   actorName   string  - display name for outgoing messages
 *   readOnly    boolean - admin observers can flip to false; defaults false
 */
export default function ChatPanel({
    clientId,
    token,
    actorType,
    actorName,
    title = "Conversation",
    subtitle = null,
    sla = null,
    onUnreadChange = null,
}) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [body, setBody] = useState("");
    const [showAttach, setShowAttach] = useState(false);
    const [attachUrl, setAttachUrl] = useState("");
    const [attachLabel, setAttachLabel] = useState("");
    const scrollRef = useRef(null);
    const lastTsRef = useRef(null);
    const intervalRef = useRef(null);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const push = usePushNotifications(token);

    const togglePush = async () => {
        if (!push.supported) {
            toast.error("Push notifications aren't supported in this browser.");
            return;
        }
        if (push.permission === "denied") {
            toast.error("Notifications are blocked. Enable them in browser settings to receive chat alerts.");
            return;
        }
        if (push.subscribed) {
            const ok = await push.unsubscribe();
            if (ok) toast.success("Push notifications turned off.");
        } else {
            const ok = await push.subscribe();
            if (ok) toast.success("You'll get a desktop alert for new messages.");
            else if (push.permission === "denied")
                toast.error("Permission denied — enable in browser settings.");
        }
    };

    const scrollBottom = () => {
        requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (el) el.scrollTop = el.scrollHeight;
        });
    };

    const markRead = useCallback(async () => {
        try {
            await axios.post(`${API}/chat/${clientId}/read`, {}, { headers });
            if (onUnreadChange) onUnreadChange(0);
        } catch {
            /* ignore */
        }
    }, [clientId, headers, onUnreadChange]);

    const fetchMessages = useCallback(
        async (initial = false) => {
            try {
                const params = {};
                if (!initial && lastTsRef.current) params.since = lastTsRef.current;
                const r = await axios.get(`${API}/chat/${clientId}/messages`, {
                    headers,
                    params,
                });
                const incoming = r.data?.messages || [];
                if (initial) {
                    setMessages(incoming);
                    if (incoming.length) {
                        lastTsRef.current = incoming[incoming.length - 1].created_at;
                    }
                    scrollBottom();
                    await markRead();
                } else if (incoming.length) {
                    setMessages((prev) => {
                        const ids = new Set(prev.map((m) => m.id));
                        const fresh = incoming.filter((m) => !ids.has(m.id));
                        if (!fresh.length) return prev;
                        const merged = [...prev, ...fresh];
                        lastTsRef.current = merged[merged.length - 1].created_at;
                        return merged;
                    });
                    scrollBottom();
                    await markRead();
                }
            } catch {
                /* swallow polling errors */
            } finally {
                if (initial) setLoading(false);
            }
        },
        [clientId, headers, markRead],
    );

    useEffect(() => {
        if (!clientId || !token) return undefined;
        setLoading(true);
        lastTsRef.current = null;
        fetchMessages(true);
        intervalRef.current = setInterval(() => fetchMessages(false), 5000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [clientId, token, fetchMessages]);

    const send = async (e) => {
        e?.preventDefault?.();
        const trimmed = body.trim();
        if (!trimmed) return;
        if (attachUrl && !/^https?:\/\//i.test(attachUrl)) {
            toast.error("Attachment must be a full URL (https://…)");
            return;
        }
        setSending(true);
        try {
            const payload = { body: trimmed };
            if (attachUrl) {
                payload.attachment_url = attachUrl;
                payload.attachment_label = attachLabel || "Attachment";
            }
            const r = await axios.post(`${API}/chat/${clientId}/messages`, payload, { headers });
            setMessages((prev) => [...prev, r.data]);
            lastTsRef.current = r.data.created_at;
            setBody("");
            setAttachUrl("");
            setAttachLabel("");
            setShowAttach(false);
            scrollBottom();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not send";
            toast.error(typeof msg === "string" ? msg : "Send failed");
        } finally {
            setSending(false);
        }
    };

    const renderMessage = (m) => {
        const mine = m.author_type === actorType && (
            actorType === "admin" ? true : m.author_id === (m.author_id /* always self for client/team */)
        );
        // Actually distinguish self by author_type+id matching; simpler: compare actorType
        const isSelf = m.author_type === actorType;
        const colorByType = {
            admin: "border-violet-400/40 text-violet-300",
            client: "border-sky-400/40 text-sky-300",
            team: "border-ember/50 text-ember",
        };
        return (
            <div
                key={m.id}
                data-testid={`chat-msg-${m.id}`}
                className={`flex ${isSelf ? "justify-end" : "justify-start"}`}
            >
                <div
                    className={`max-w-[78%] border px-4 py-3 ${
                        isSelf
                            ? "border-ember/40 bg-ember/5"
                            : "border-white/10 bg-ink-800/50"
                    }`}
                >
                    <div className="flex items-center gap-2 mb-1.5 text-[10px] uppercase tracking-[0.2em]">
                        <span className={`px-1.5 py-0.5 border ${colorByType[m.author_type] || "border-white/15 text-bone-500"}`}>
                            {m.author_type}
                        </span>
                        <span className="text-bone-500 normal-case tracking-normal">
                            {m.author_name}
                        </span>
                        <span className="text-bone-500 normal-case tracking-normal ml-auto">
                            {new Date(m.created_at).toLocaleString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                month: "short",
                                day: "numeric",
                            })}
                        </span>
                    </div>
                    <p className="text-sm text-bone whitespace-pre-wrap leading-relaxed">
                        {m.body}
                    </p>
                    {m.mentioned_manager_name && (
                        <div
                            data-testid={`chat-msg-mention-${m.id}`}
                            className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 border border-ember/40 bg-ember/5 text-ember text-[10px] uppercase tracking-[0.2em]"
                        >
                            → @{m.mentioned_manager_name}
                        </div>
                    )}
                    {m.attachment_url && (
                        <a
                            href={m.attachment_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="mt-2 inline-flex items-center gap-2 text-xs text-ember link-underline"
                            data-testid={`chat-msg-attach-${m.id}`}
                        >
                            <Paperclip className="w-3.5 h-3.5" />
                            {m.attachment_label || m.attachment_url}
                        </a>
                    )}
                </div>
            </div>
        );
    };

    return (
        <section
            data-testid="chat-panel"
            className="border border-white/10 bg-ink-800/40 flex flex-col h-[560px]"
        >
            <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <MessageSquare className="w-4 h-4 text-ember" />
                    <div>
                        <h3 className="font-display text-bone text-base tracking-tight">
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="text-[11px] text-bone-500 mt-0.5">{subtitle}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {push.supported && (
                        <button
                            type="button"
                            onClick={togglePush}
                            disabled={push.busy}
                            data-testid="chat-push-toggle"
                            title={
                                push.permission === "denied"
                                    ? "Notifications blocked — enable in browser settings"
                                    : push.subscribed
                                      ? "Disable desktop notifications"
                                      : "Enable desktop notifications for new messages"
                            }
                            className={`h-8 w-8 inline-flex items-center justify-center border transition-colors ${
                                push.subscribed
                                    ? "border-ember/60 text-ember"
                                    : "border-white/15 text-bone-500 hover:border-white/30 hover:text-bone"
                            } ${push.busy ? "opacity-50" : ""}`}
                        >
                            {push.busy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : push.subscribed ? (
                                <Bell className="w-3.5 h-3.5" />
                            ) : (
                                <BellOff className="w-3.5 h-3.5" />
                            )}
                        </button>
                    )}
                    <span className="text-[10px] uppercase tracking-[0.25em] text-bone-500">
                        {actorName} · {actorType}
                    </span>
                </div>
            </header>

            {sla && sla.has_manager && sla.response_hours && (
                <div
                    data-testid="chat-sla-banner"
                    className={`px-5 py-2.5 border-b text-[11px] flex items-center justify-between gap-3 ${
                        sla.in_business_hours
                            ? "border-emerald-400/20 bg-emerald-400/5"
                            : "border-white/10 bg-ink-800/30"
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span
                            className={`inline-block w-1.5 h-1.5 rounded-full ${
                                sla.in_business_hours ? "bg-emerald-400" : "bg-bone-500"
                            }`}
                        />
                        <span className={sla.in_business_hours ? "text-emerald-400" : "text-bone-500"}>
                            {sla.in_business_hours ? "Manager online" : "Outside business hours"}
                        </span>
                        <span className="text-bone-500">·</span>
                        <span className="text-bone-300">
                            {sla.in_business_hours
                                ? `Replies within ${sla.response_hours}h`
                                : `Replies resume ${sla.business_hours_label}`}
                        </span>
                    </div>
                </div>
            )}

            <div
                ref={scrollRef}
                data-testid="chat-scroll"
                className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
            >
                {loading ? (
                    <div className="h-full flex items-center justify-center text-bone-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-center">
                        <div>
                            <MessageSquare className="w-8 h-8 text-bone-500 mx-auto mb-3" />
                            <p className="text-sm text-bone-500">
                                No messages yet. Start the conversation.
                            </p>
                        </div>
                    </div>
                ) : (
                    messages.map(renderMessage)
                )}
            </div>

            <form
                onSubmit={send}
                data-testid="chat-form"
                className="border-t border-white/10 p-4 space-y-3"
            >
                {showAttach && (
                    <div className="border border-white/10 bg-ink p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-[0.25em] text-ember">
                                Attach Drive / Dropbox link
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowAttach(false);
                                    setAttachUrl("");
                                    setAttachLabel("");
                                }}
                                className="text-bone-500 hover:text-bone"
                                data-testid="chat-attach-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <input
                            type="url"
                            value={attachUrl}
                            onChange={(e) => setAttachUrl(e.target.value)}
                            placeholder="https://drive.google.com/…"
                            data-testid="chat-attach-url"
                            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm text-bone placeholder:text-bone-500 focus:border-ember outline-none"
                        />
                        <input
                            type="text"
                            value={attachLabel}
                            onChange={(e) => setAttachLabel(e.target.value)}
                            placeholder="Label (optional)"
                            data-testid="chat-attach-label"
                            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm text-bone placeholder:text-bone-500 focus:border-ember outline-none"
                        />
                    </div>
                )}
                <div className="flex items-end gap-2">
                    <button
                        type="button"
                        onClick={() => setShowAttach((v) => !v)}
                        data-testid="chat-attach-toggle"
                        className={`h-10 w-10 inline-flex items-center justify-center border transition-colors ${
                            showAttach || attachUrl
                                ? "border-ember text-ember"
                                : "border-white/15 text-bone-500 hover:border-white/30 hover:text-bone"
                        }`}
                        aria-label="Attach link"
                    >
                        <Paperclip className="w-4 h-4" />
                    </button>
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Write a message…"
                        rows={2}
                        data-testid="chat-input"
                        className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm text-bone placeholder:text-bone-500 focus:border-ember outline-none resize-none"
                    />
                    <button
                        type="submit"
                        disabled={sending || !body.trim()}
                        data-testid="chat-send"
                        className="btn-ember h-10 px-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] disabled:opacity-50"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send
                    </button>
                </div>
            </form>
        </section>
    );
}
